import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { RuleGroupsEditor } from "@/components/apply-to/RuleGroupsEditor.tsx";
import { ruleGroupsSchema } from "@/components/apply-to/rule-groups.schema.ts";
import {
  useRecognitionCategory,
  useRecognitionCategorySave,
} from "@/queries/use-recognitions.query.ts";
import { useRuleConditionTypes } from "@/queries/use-rules.query.ts";
import { RuleRegistryPlace } from "@/types/rule-whitelists.ts";
import { getRecognitionGiftRuleGroups } from "@/types/recognitions/recognition.types.ts";
import { Rule, RuleApplySignType, RuleConditionType } from "@/types/rule.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Input,
  Modal,
  ModalProps,
  Skeleton,
  Stack,
  Textarea,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const defaultGiftRule = {
  condition_type: RuleConditionType.all_active_employees,
  sign: RuleApplySignType.include,
  condition_ids: [],
  value_ids: [],
  condition_entities: [],
  value_entities: [],
} satisfies Rule;

export interface RecognitionCategoryEditModalProps extends ModalProps {
  categoryId?: number;
}

export function RecognitionCategoryEditModal({
  categoryId,
  ...props
}: RecognitionCategoryEditModalProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const { id } = props;

  const isEdit = categoryId !== undefined;
  const { data: category, isLoading } = useRecognitionCategory(categoryId);

  const giftConditionTypes = useRuleConditionTypes(RuleRegistryPlace.recognitionCategoryGift);

  const categorySchema = z.object({
    title: z.string().trim().min(1, t("errors:field_required")),
    description: z.string(),
    gift_apply_to_groups: ruleGroupsSchema(),
  });
  type CategorySchema = z.infer<typeof categorySchema>;

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<CategorySchema>({
    mode: "onSubmit",
    resolver: zodResolver(categorySchema),
    defaultValues: {
      title: "",
      description: "",
      gift_apply_to_groups: [{ rules: [defaultGiftRule] }],
    },
    values: category
      ? {
          title: category.title,
          description: category.description,
          gift_apply_to_groups: getRecognitionGiftRuleGroups(category).map((group) => ({
            rules: group.rules.map((rule) => ({ ...rule, value_ids: rule.value_ids ?? [] })),
          })),
        }
      : undefined,
  });

  const isCategoryLoading = isEdit && isLoading;

  const categorySave = useRecognitionCategorySave(control);

  const onSubmit = handleSubmit(async (data) => {
    try {
      await categorySave.mutateAsync({
        id: category?.id,
        payload: {
          title: data.title.trim(),
          description: data.description.trim(),
          gift_apply_to_groups: data.gift_apply_to_groups.filter((group) => group.rules.length > 0),
        },
      });
      close(id);
    } catch {
      return;
    }
  });

  return (
    <Modal
      {...props}
      title={t(isEdit ? "recognition:category_edit_title" : "recognition:category_create_title")}
      withCloser
      layout={"stretch"}
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            type={"submit"}
            size={"lg"}
            isLoading={categorySave.isPending}
            disabled={isCategoryLoading}
            onClick={onSubmit}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        {isCategoryLoading ? (
          <Stack gap={"md"}>
            <Skeleton className={"h-12 w-full"} />
            <Skeleton className={"h-24 w-full"} />
            <Skeleton className={"h-24 w-full"} />
          </Stack>
        ) : (
          <Stack gap={"md"}>
            <Input
              required={true}
              label={t("common:title")}
              autoFocus={true}
              type={"text"}
              {...register("title")}
              error={errors.title?.message}
            />
            <Textarea
              label={t("common:description")}
              {...register("description")}
              error={errors.description?.message}
            />
            <Stack gap={"sm"}>
              <Title
                header={t("recognition:gift_rules_title")}
                caption={t("recognition:gift_rules_caption")}
                paddingBottom
                size={"xs"}
              />
              <RuleGroupsEditor
                control={control}
                setValue={setValue}
                fieldName="gift_apply_to_groups"
                place={RuleRegistryPlace.recognitionCategoryGift}
                conditionTypes={giftConditionTypes.conditionTypes}
                variant="list"
                error={errors.gift_apply_to_groups?.message}
              />
            </Stack>
          </Stack>
        )}
      </form>
    </Modal>
  );
}
